/*
 * preorder-status.js — 「販売開始前」表示のクライアント側制御
 *
 * 役割は2つ:
 *   (A) 時刻到来の二重判定（§7）: server が dated と判定した datebox / badge を、
 *       ページ表示中に開始時刻へ到達したら反映する。対象は data-start-ts を持つ全要素。
 *         - datebox (data-pg-role="datebox"): 空白化を避けるため「再読み込みを促す表示」に差し替え。
 *         - badge   (data-pg-role="badge")  : 非表示 (hidden=true)。
 *       coming_soon の badge は data-start-ts を持たないため対象外・不干渉。
 *       ※ server 側判定が唯一の真実。JS はページ滞在中の"追い越し"のみ補正する。
 *
 *   (B) バリアント切替（PDP）: バリアント別 Pre-order_start_date に対応するため、
 *       custom element <preorder-status> で .js-preorder-variant[data-variant-id] を
 *       on:variant:change で出し分ける。切替後に (A) の時刻判定を再適用する。
 *
 * 購入ロジックは一切扱わない（available が唯一の真実）。表示層のみ。
 *
 * サーバー時刻補正は行わない: 補正基準に使えるサーバー時刻は Liquid の 'now' しかなく、
 * これはページキャッシュで固まる（§7でJS二重判定を入れた理由そのもの）。キャッシュ済みの
 * now を基準にすると到来判定がキャッシュ経過分だけズレるため、補正は逆効果。
 * クライアント時計ズレで起きるのは「余計な再読み込み促し」か「表示が残る」だけで、
 * いずれも購入可否には無影響・軽微。ゆえに nowSec() は Date.now() を素で用いる。
 */

(function () {
  'use strict';

  // setTimeout の 32bit オーバーフロー回避（約24.8日）。これを超える先はタイマーを張らず、
  // server 再訪時の再判定に委ねる（長時間放置ページの正確性は要件外）。
  var MAX_TIMEOUT_MS = 2147483000;

  function nowSec() {
    return Math.floor(Date.now() / 1000);
  }

  // 到来後に再処理されないよう data-start-ts を除去し状態を arrived に更新
  function markArrived(el) {
    el.removeAttribute('data-start-ts');
    el.setAttribute('data-preorder-state', 'arrived');
  }

  // datebox を「再読み込みを促す表示」へ差し替え（空白化の回避）。
  // location.reload() は必ずユーザークリック起点。自動実行はしない。
  function replaceWithReloadPrompt(el) {
    var btnClass = el.dataset.reloadBtnClass || 'btn'; // 最終クラスはステップ5で確定
    el.textContent = '';
    var msg = document.createElement('span');
    msg.className = 'pg-datebox__text';
    msg.textContent = '販売を開始しました。ページを再読み込みしてください。';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = btnClass;
    btn.textContent = '再読み込み';
    btn.addEventListener('click', function () {
      window.location.reload();
    });
    el.appendChild(msg);
    el.appendChild(btn);
  }

  // 到来時の反映（role で分岐）
  function handleArrival(el) {
    if (el.dataset.pgRole === 'datebox') {
      replaceWithReloadPrompt(el);
    } else {
      el.hidden = true;
    }
    markArrived(el);
  }

  // 単一要素に到来判定を適用（到来済みは即反映 / 未来は到来時刻に予約）
  function applyArrival(el) {
    if (!el || el.dataset.pgArrivalBound === '1') return;
    var ts = parseInt(el.dataset.startTs, 10);
    if (!ts || isNaN(ts)) return; // start_ts 無し（coming_soon）は対象外
    var remainSec = ts - nowSec();
    if (remainSec <= 0) {
      handleArrival(el);
      return;
    }
    var ms = remainSec * 1000;
    if (ms > MAX_TIMEOUT_MS) return; // 遠すぎる未来はタイマー未設定
    el.dataset.pgArrivalBound = '1';
    setTimeout(function () {
      handleArrival(el);
    }, ms);
  }

  // ルート配下（既定は document）の data-start-ts 要素すべてに到来判定を適用
  function scanArrival(root) {
    var scope = root || document;
    var els = scope.querySelectorAll('[data-start-ts]');
    for (var i = 0; i < els.length; i++) {
      applyArrival(els[i]);
    }
  }

  function initArrival() {
    scanArrival(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initArrival);
  } else {
    initArrival();
  }

  // (B) PDP バリアント切替。
  if (!customElements.get('preorder-status')) {
    class PreorderStatus extends HTMLElement {
      constructor() {
        // 仕様上、コンストラクタでは子要素・属性を参照しない（super のみ）。
        super();
      }

      connectedCallback() {
        // 遅延初期化はしない: 販売可否に関わる情報のため、購読の取りこぼしを避け即時初期化する。
        this.variantNodes = this.querySelectorAll('.js-preorder-variant');
        if (!this.variantNodes.length) return;

        // 購読先は要素名やテーマ独自クラスに依存させず、Shopify が保証する
        // セクションラッパ (.shopify-section) にスコープする。on:variant:change は
        // bubbles: true のため同一セクション内で受けられ、関連商品/クイックビュー
        // （別セクション）のイベントは届かない。
        this.section = this.closest('.shopify-section');
        this.scope = this.section || document;
        this.onVariantChangeBound = this.onVariantChange.bind(this);
        this.scope.addEventListener('on:variant:change', this.onVariantChangeBound);

        if (this.dataset.currentVariantId) {
          this.setVisible(this.dataset.currentVariantId);
        }
      }

      disconnectedCallback() {
        if (this.scope && this.onVariantChangeBound) {
          this.scope.removeEventListener('on:variant:change', this.onVariantChangeBound);
        }
      }

      setVisible(variantId) {
        var target = variantId != null ? variantId.toString() : '';
        this.variantNodes.forEach(function (node) {
          node.hidden = node.dataset.variantId.toString() !== target;
        });
        // 表示された node にも到来判定を適用（pgArrivalBound で二重処理防止）
        scanArrival(this);
      }

      onVariantChange(evt) {
        if (!evt.detail || !evt.detail.variant) return;
        // document へフォールバックした場合の保険: 発火元が同一セクションかを検証し、
        // 複数商品同居ページ（関連商品/クイックビュー）での誤作動を防ぐ。
        if (this.scope === document && this.section && evt.target && evt.target.closest) {
          if (evt.target.closest('.shopify-section') !== this.section) return;
        }
        this.setVisible(evt.detail.variant.id);
      }
    }

    customElements.define('preorder-status', PreorderStatus);
  }
})();
